import React, { useMemo, useRef, useState } from 'react';
import { Select, Spin } from 'antd';
import type { SelectProps } from 'antd/es/select';
import debounce from 'lodash/debounce';
import { buildURL } from '@/fetch/core/util';

export interface DebounceSelectProps<ValueType = any> extends Omit<
  SelectProps<ValueType | ValueType[]>,
  'options' | 'children'
> {
  fetchOptions: (search: string) => Promise<ValueType[]>;
  debounceTimeout?: number;
  placeholder?: string;
}

function DebounceSelect<
  ValueType extends {
    key?: string;
    label: React.ReactNode;
    value: string | number;
    placeholder?: string;
  } = any,
>({
  fetchOptions,
  debounceTimeout = 800,
  ...props
}: DebounceSelectProps<ValueType>) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>([]);
  const fetchRef = useRef(0);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          return;
        }

        setOptions(newOptions);
        setFetching(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  return (
    <Select
      showSearch
      labelInValue
      filterOption={false}
      allowClear
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      options={options}
    />
  );
}

interface Poi {
  label: string;
  value: string;
}

const fetchUserList = (searchText: string): Promise<Poi[]> => {
  return new Promise((resolve, reject) => {
    const win = window as any;
    win.$.ajax({
      type: 'get',
      url: buildURL({
        absoluteURL: 'https://apis.map.qq.com/ws/place/v1/suggestion',
        urlParams: {
          key: '67ABZ-HLPRQ-XZM53-GNCSR-GNSXF-DSBDK',
          keyword: encodeURI(searchText),
          get_subpois: 1,
          output: 'jsonp',
        },
      }),
      dataType: 'jsonp',
      success: (res: any) => {
        if (res.status == 0) {
          const result = res?.data?.map((item: any) => {
            return {
              label: item.title,
              value: item?.location?.lng + '-' + item?.location?.lat,
            };
          });
          resolve(result);
        }
      },
    });
  });
};

const DebounceAsyncSelect = (props: {
  onChange: AnyFunc;
  placeholder?: string;
  defaultValue?: string;
}) => {
  const [value, setValue] = useState<Poi[]>([]);

  return (
    <DebounceSelect
      value={value}
      placeholder={props?.placeholder}
      fetchOptions={fetchUserList}
      onChange={(newValue) => {
        setValue(newValue as Poi[]);
        props?.onChange(newValue);
      }}
      style={{ width: '100%' }}
    />
  );
};

export default React.memo(DebounceAsyncSelect);
